import React, { useState } from 'react';
import { Card, Button, Table, Input, Space, App, Alert } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ownerIntelligenceService from '@/services/ownerIntelligence.service';
import { OiPageFrame, OiEmptyState } from '@/components/owner-intelligence/OwnerIntelligenceComponents';
import { StrategyContextStrip } from '@/components/owner-intelligence/StrategyContextStrip';

const BoardPackPage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [packName, setPackName] = useState('');
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportingPptxId, setExportingPptxId] = useState<string | null>(null);
  const { data: packs, isLoading } = useQuery({ queryKey: ['owner-intelligence', 'board-packs'], queryFn: () => ownerIntelligenceService.listBoardPacks() });
  const { data: briefs } = useQuery({ queryKey: ['owner-intelligence', 'briefs'], queryFn: () => ownerIntelligenceService.listBriefs() });

  const createPack = async () => {
    try {
      await ownerIntelligenceService.createBoardPack({ pack_name: packName || `Board Pack ${new Date().toLocaleDateString()}` });
      message.success('Board pack created');
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'board-packs'] });
      setPackName('');
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const generateBrief = async () => {
    try {
      await ownerIntelligenceService.generateBrief();
      message.success('Daily brief generated');
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'briefs'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const approvePack = async (id: string) => {
    try {
      await ownerIntelligenceService.approveBoardPack(id);
      message.success('Board pack approved');
      qc.invalidateQueries({ queryKey: ['owner-intelligence', 'board-packs'] });
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const exportBrief = async (id: string) => {
    try {
      const blob = await ownerIntelligenceService.exportBrief(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brief-${id}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      message.error((e as Error).message);
    }
  };

  const downloadPdf = async (id: string, name: string) => {
    try {
      setExportingId(id);
      const blob = await ownerIntelligenceService.exportBoardPackPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.replace(/[^\w-]+/g, '_')}_board_pack.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Board pack PDF downloaded');
    } catch (e: unknown) {
      message.error((e as Error).message);
    } finally {
      setExportingId(null);
    }
  };

  const downloadPptx = async (id: string, name: string) => {
    try {
      setExportingPptxId(id);
      const blob = await ownerIntelligenceService.exportBoardPackPptx(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name.replace(/[^\w-]+/g, '_')}_board_pack.pptx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Board pack PowerPoint downloaded');
    } catch (e: unknown) {
      message.error((e as Error).message);
    } finally {
      setExportingPptxId(null);
    }
  };

  const packRows = (packs as any[]) ?? [];
  const briefRows = (briefs as any[]) ?? [];

  return (
    <OiPageFrame
      subtitle="Board packs, daily briefs, and executive memory. Pack sections are assembled when you export PDF/PPT."
      loading={isLoading && !packs}
    >
      <StrategyContextStrip compact />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Preview on export"
        description="Board pack sections are built from live ERP data when you download PDF or PowerPoint — the list view shows metadata only."
      />
      <Card title="Daily briefing" extra={<Button type="primary" onClick={generateBrief}>Generate today&apos;s brief</Button>} style={{ marginBottom: 16 }} bordered={false}>
        <Table
          size="small"
          rowKey="id"
          loading={!briefs}
          dataSource={briefRows}
          locale={{
            emptyText: (
              <OiEmptyState
                title="No briefs yet"
                body="Generate today's executive brief to capture health, cash, and risk highlights."
                actionLabel="Generate brief"
                onAction={generateBrief}
              />
            ),
          }}
          columns={[
            { title: 'Date', dataIndex: 'brief_date' },
            { title: 'Title', dataIndex: 'title' },
            { title: 'Type', dataIndex: 'brief_type' },
            {
              title: '',
              key: 'exp',
              render: (_: unknown, row: { id: string }) => (
                <Button size="small" onClick={() => exportBrief(row.id)}>Export</Button>
              ),
            },
          ]}
        />
      </Card>
      <Card
        title="Board packs"
        bordered={false}
        extra={
          <Space>
            <Input placeholder="Pack name" value={packName} onChange={(e) => setPackName(e.target.value)} />
            <Button onClick={createPack}>New pack</Button>
          </Space>
        }
      >
        <Table
          size="small"
          rowKey="id"
          dataSource={packRows}
          locale={{
            emptyText: (
              <OiEmptyState
                title="No board packs"
                body="Create a pack to assemble KPIs, risks, and decisions for your next board meeting."
                actionLabel="Create pack"
                onAction={createPack}
              />
            ),
          }}
          columns={[
            { title: 'Name', dataIndex: 'pack_name' },
            { title: 'Version', dataIndex: 'pack_version' },
            { title: 'Status', dataIndex: 'status' },
            {
              title: '',
              key: 'export',
              render: (_: unknown, row: { id: string; pack_name: string; status: string }) => (
                <Space>
                  <Button size="small" icon={<DownloadOutlined />} loading={exportingId === row.id} onClick={() => downloadPdf(row.id, row.pack_name)}>PDF</Button>
                  <Button size="small" loading={exportingPptxId === row.id} onClick={() => downloadPptx(row.id, row.pack_name)}>PPT</Button>
                  {row.status !== 'approved' && <Button size="small" onClick={() => approvePack(row.id)}>Approve</Button>}
                </Space>
              ),
            },
          ]}
        />
      </Card>
    </OiPageFrame>
  );
};

export default BoardPackPage;
